// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ScrollIndicator } from '@/components/scroll-indicator';
import { SupportSnackbar } from '@/components/support-snackbar';
import { useScrollIndicator } from '@/hooks/use-scroll-indicator';
import { getItem } from '@/items/api/get-item';
import { MergeItemParameters } from '@/items/api/merge-item';
import { ItemDetail } from '@/items/api/types';
import { ItemSelectionList } from '@/items/components/item-selection-list';
import { FieldComparisonTable } from '@/items/components/merge-item/components/field-comparison-table';
import { ItemComparisonCard } from '@/items/components/merge-item/components/item-comparison-card';
import { MergeItemHeader } from '@/items/components/merge-item/components/merge-item-header';
import { ValidationErrorDisplay } from '@/items/components/merge-item/components/validation-error-display';
import { useFieldSelections } from '@/items/components/merge-item/hooks/use-field-selections';
import { useMergeItem } from '@/items/components/merge-item/hooks/use-merge-item';
import { useMergeValidation } from '@/items/components/merge-item/hooks/use-merge-validation';
import { prepareFieldUpdates } from '@/items/components/merge-item/utilities/merge-utilities';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Suspense, useEffect, useState } from 'react';

interface Props {
  isOpen: boolean;
  item: ItemDetail;
  onClose: () => void;
}

const steps = ['Select item to merge', 'Select data fields to merge'];

function LoadingPlaceholder() {
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="400px"
      p={3}
    >
      <Stack spacing={2} alignItems="center">
        <CircularProgress />
        <Typography variant="body1">Loading...</Typography>
      </Stack>
    </Box>
  );
}

function SelectItemStep(props: {
  item: ItemDetail;
  onItemSelect: (itemKey: string) => void;
  isLoading: boolean;
}) {
  return (
    <Stack spacing={3}>
      <Grid container sx={{ px: 8 }}>
        <Grid item xs={6} sx={{ display: 'flex', pr: 2 }}>
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <ItemComparisonCard item={props.item} />
          </Box>
        </Grid>
        <Grid item xs={6} sx={{ display: 'flex', pl: 2 }}>
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle1" gutterBottom>
              Select {props.item.type.toLowerCase()} item to merge into{' '}
              {props.item.name}
            </Typography>
            <Box sx={{ flexGrow: 1, position: 'relative' }}>
              {props.isLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    zIndex: 1,
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}
              <ItemSelectionList
                type={props.item.type}
                onItemSelect={props.onItemSelect}
                excludeItemId={props.item.id}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Stack>
  );
}

export function MergeItemDialog(props: Props) {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedItemKey, setSelectedItemKey] = useState<string | null>(null);
  const [pendingValidation, setPendingValidation] = useState(false);

  const scrollIndicator = useScrollIndicator();

  const validation = useMergeValidation();
  const {
    fieldSelections,
    handleFieldSelectionChange,
    initializeSelections,
    resetSelections,
  } = useFieldSelections();

  const { mergeItems, isLoading, error, showSnackbar, clearError } =
    useMergeItem(() => {
      handleClose();
    });

  const { data: selectedItemData, isLoading: isLoadingSelectedItem } = useQuery(
    {
      queryKey: ['item', selectedItemKey],
      queryFn: () => getItem(selectedItemKey!),
      enabled: !!selectedItemKey,
    }
  );

  const selectedItem = selectedItemData?.item;

  // Handle validation after item details are loaded
  useEffect(() => {
    if (pendingValidation && selectedItem && !isLoadingSelectedItem) {
      setPendingValidation(false);

      if (!validation.validate(props.item, selectedItem)) {
        // Validation failed, stay on step 0
        setSelectedItemKey(null);
        return;
      }

      // Validation passed, proceed to step 2
      initializeSelections(props.item, selectedItem);
      setActiveStep(1);
    }
  }, [
    pendingValidation,
    selectedItem,
    isLoadingSelectedItem,
    props.item,
    validation,
    initializeSelections,
  ]);

  const handleItemSelect = (itemKey: string) => {
    setSelectedItemKey(itemKey);
    setPendingValidation(true);
  };

  const handleMergeSubmit = () => {
    if (!selectedItem) return;

    const fieldUpdates = prepareFieldUpdates(
      props.item,
      selectedItem,
      fieldSelections
    );

    const bomSelection = fieldSelections.bom || 'primary';
    const bomItemId =
      bomSelection === 'primary' ? props.item.id : selectedItem.id;

    const parameters: MergeItemParameters = {
      removedItemId: selectedItem.id,
      mergedItemName: fieldUpdates.name || props.item.name,
      mergedItemSku: fieldUpdates.sku ?? '',
      mergedItemBomItemId: bomItemId,
    };

    mergeItems(props.item.key, parameters);
  };

  const handleClose = () => {
    setActiveStep(0);
    setSelectedItemKey(null);
    setPendingValidation(false);
    validation.clearError();
    clearError();
    resetSelections();
    props.onClose();
  };

  const handleBack = () => {
    setActiveStep(0);
    setSelectedItemKey(null);
    setPendingValidation(false);
    validation.clearError();
    clearError();
    resetSelections();
  };

  const validationError = validation.error;
  const mergeError = error;

  const handleClickScroll = () => {
    if (scrollIndicator.element === null) {
      return;
    }

    scrollIndicator.element.scrollTo({
      top: scrollIndicator.element.scrollHeight,
      behavior: 'smooth',
    });

    scrollIndicator.hide();
  };

  return (
    <Dialog open={props.isOpen} onClose={handleClose} maxWidth="lg" fullWidth>
      <SupportSnackbar
        isOpen={showSnackbar}
        message="An error occurred while merging the items."
        onClose={clearError}
      />

      <DialogTitle>
        Merge {props.item.type.toLowerCase()} items
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Select another {props.item.type.toLowerCase()} item to merge into{' '}
          {props.item.name}
        </Typography>
      </DialogTitle>

      <MergeItemHeader
        primaryItemName={props.item.name}
        selectedItemName={selectedItem?.name || null}
      />

      <DialogContent sx={{ p: 0 }}>
        <Suspense fallback={<LoadingPlaceholder />}>
          {/* Non-scrollable content */}
          <Box sx={{ p: 3, pb: 0 }}>
            <Box sx={{ mb: 3 }}>
              <Stepper
                alternativeLabel
                activeStep={activeStep}
                sx={{
                  '& .MuiStepIcon-root': {
                    fontSize: '2.25rem',
                  },
                  '& .MuiStepIcon-text': {
                    fontWeight: 'bold',
                  },
                  '& .MuiStepConnector-line': {
                    borderTopWidth: '1px',
                  },
                  '& .MuiStepConnector-root': {
                    top: '18px',
                  },
                }}
              >
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            {validationError && (
              <Box sx={{ pb: 2 }}>
                <Alert severity="error">
                  <ValidationErrorDisplay error={validationError} />
                </Alert>
              </Box>
            )}

            {mergeError && (
              <Box sx={{ pb: 2 }}>
                <Alert severity="error">{mergeError}</Alert>
              </Box>
            )}
          </Box>

          {/* Scrollable content with indicator */}
          <Box sx={{ px: 3, pb: 3 }}>
            {' '}
            {/* Outer container - padding */}
            <Box sx={{ position: 'relative' }}>
              {' '}
              {/* Middle container - positioning context */}
              <Box
                ref={scrollIndicator.ref}
                sx={{
                  maxHeight: '60vh',
                  overflow: 'auto',
                }}
              >
                {activeStep === 0 && (
                  <SelectItemStep
                    item={props.item}
                    onItemSelect={handleItemSelect}
                    isLoading={pendingValidation && isLoadingSelectedItem}
                  />
                )}

                {activeStep === 1 && (
                  <>
                    {isLoadingSelectedItem ? (
                      <LoadingPlaceholder />
                    ) : selectedItem ? (
                      <FieldComparisonTable
                        primaryItem={props.item}
                        selectedItem={selectedItem}
                        fieldSelections={fieldSelections}
                        onFieldSelectionChange={handleFieldSelectionChange}
                      />
                    ) : (
                      <Box
                        minHeight="400px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        p={3}
                      >
                        <Alert severity="error">
                          Failed to load item details. Please try again.
                        </Alert>
                      </Box>
                    )}
                  </>
                )}
              </Box>
              <ScrollIndicator
                label="More"
                isVisible={scrollIndicator.isVisible}
                onClick={handleClickScroll}
              />
            </Box>
          </Box>
        </Suspense>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', m: 1 }}>
        {activeStep === 0 ? (
          <Button onClick={handleClose}>Cancel</Button>
        ) : (
          <>
            <Button onClick={handleBack}>Back</Button>
            <Button
              onClick={handleMergeSubmit}
              variant="contained"
              disabled={isLoading || isLoadingSelectedItem || !selectedItem}
            >
              {isLoading ? 'Merging...' : 'Confirm merge'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
